import { useState } from 'react'
import type { AssignmentCompletion, Job, JobAssignment, UpdateAssignmentInput } from '@workflow/contracts'
import { ActionMenu, Avatar, Button, Dialog, Dropdown, EmptyNote, Field, IconButton, Icons, Loading, StateNote, StatusLabel, TextArea, TextField } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useAssignPlayers, useAssignments, useSetAssignmentCompletion, useSetJobStatus, useUpdateAssignment, useDeductAssignmentHours, useAssignmentHourAdjustments, useNotes, useSession } from '@/lib/api/hooks'
import { formatTimestamp } from '@/lib/format'
import { AssignmentPanel, newAssignmentDraft, resolveAssignments } from './AssignmentPanel'
import { partState, formatHours } from './jobsStatus'

/**
 * The verbs an assignment offers, by the state it is in. Dropping and
 * removing open a reason field before they act — the reason is the record,
 * and for a drop the API refuses to do without one. A completion can be
 * reopened, because a mark made on a player's behalf can be wrong.
 */
const VERBS: Record<AssignmentCompletion, readonly { target: AssignmentCompletion; verb: string; asksReason?: boolean }[]> = {
  assigned: [
    { target: 'in_progress', verb: 'Mark In Progress' },
    { target: 'completed', verb: 'Mark Completed' },
    { target: 'dropped', verb: 'Mark Dropped', asksReason: true },
    { target: 'removed', verb: 'Remove', asksReason: true },
  ],
  in_progress: [
    { target: 'completed', verb: 'Mark Completed' },
    { target: 'dropped', verb: 'Mark Dropped', asksReason: true },
    { target: 'removed', verb: 'Remove', asksReason: true },
  ],
  completed: [{ target: 'in_progress', verb: 'Reopen' }],
  dropped: [{ target: 'assigned', verb: 'Put Back on the Job' }],
  removed: [{ target: 'assigned', verb: 'Put Back on the Job' }],
}

/**
 * The crew of a job: the players on it, the hours each holds, what has been
 * completed of the work's size, and where each player stands. Assigning is a
 * decision about a person, so the player list is the active players only —
 * and one player sits on a job exactly once.
 */
export function JobCrew({ jobId, job }: { jobId: string; job: Job }) {
  const assignments = useAssignments(jobId)
  const assign = useAssignPlayers()
  const [assigning, setAssigning] = useState(false)
  const crew = assignments.data ?? []
  const active = crew.filter(({ completion }) => completion !== 'dropped' && completion !== 'removed')
  const canAssign = !job.archivedAt && !['completed', 'removed', 'cancelled'].includes(job.status)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3 border-b border-hairline pb-4">
        <div className="min-w-0 flex-1 basis-56">
          <p className="text-ui font-medium text-foreground">{job.playersNeeded == null ? `${active.length} ${active.length === 1 ? 'Player' : 'Players'} Assigned` : `${active.length} of ${job.playersNeeded} Players Assigned`}</p>
          <HoursSummary job={job} crew={crew} />
        </div>
        {canAssign ? <Button size="sm" variant="primary" icon={<Icons.plus />} disabled={assignments.isLoading || Boolean(assignments.error)} onClick={() => {
          assign.reset()
          setAssigning(true)
        }}>Assign players</Button> : null}
      </div>
      {assigning ? <AssignmentPanel
        initial={newAssignmentDraft(job.playerRateAmount === null ? '' : String(job.playerRateAmount / 100))}
        blocked={active.map(({ playerId }) => playerId)}
        draftJob={job.status === 'draft'}
        currency={`${job.currency ?? 'BDT'}${job.playerRateType === 'hourly' ? '/h' : ''}`}
        busy={assign.isPending}
        error={assign.error}
        onClose={() => setAssigning(false)}
        onApply={(draft, notify) => assign.mutate({ jobId, input: { assignments: resolveAssignments(draft), notify } }, { onSuccess: () => setAssigning(false) })}
      /> : null}

      <SettledBanner job={job} crew={crew} />

      {assignments.isLoading ? (
        <Loading label="Loading the crew" />
      ) : assignments.error ? (
        <StateNote title="The crew did not load." detail={describeError(assignments.error)} />
      ) : crew.length === 0 ? (
        <EmptyNote title="No one is on this job yet." detail="Assign a player to this job." />
      ) : (
        <ul className="divide-y divide-hairline">
          {crew.map((assignment) => (
            <AssignmentRow key={assignment.id} jobId={jobId} assignment={assignment} />
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * The hours summary reads alongside the player count. Oversubscription is
 * shown, not refused: a crew may deliberately hold more hands than the pool,
 * and the line says so rather than quietly allowing it.
 */
function HoursSummary({ job, crew }: { job: Job; crew: readonly JobAssignment[] }) {
  const pool = job.estimatedHours
  const active = crew.some((a) => a.completion === 'assigned' || a.completion === 'in_progress' || a.completion === 'completed')
  if (pool === null && !active && job.completedHours === 0 && !job.loggedHours) return null

  const over = pool !== null && job.assignedHours > pool
  const remaining = pool === null ? null : pool - job.assignedHours
  return (
    <p className="mt-1 min-w-0 text-caption leading-4 text-muted-foreground">
      <span className="font-medium text-foreground">{formatHours(job.completedHours)} completed</span>
      {pool !== null ? ` of ${formatHours(pool)} estimated` : ''}
      <span aria-hidden> · </span>
      <span className={over ? 'text-danger' : undefined}>
        {formatHours(job.assignedHours)} assigned{over ? ' — over the pool' : ''}
      </span>
      {remaining !== null && remaining > 0 ? ` · ${formatHours(remaining)} unassigned` : null}
      {job.loggedHours > 0 && job.loggedHours !== job.completedHours ? ` · ${formatHours(job.loggedHours)} logged` : null}
    </p>
  )
}

/**
 * The end of the hours story: when nobody is at work anymore but the work
 * happened, the job is ready to be completed — said here, where the crew
 * was just read, with the verb to do it.
 */
function SettledBanner({ job, crew }: { job: Job; crew: readonly JobAssignment[] }) {
  const move = useSetJobStatus()
  if (job.status !== 'in_progress') return null
  const atWork = crew.some((a) => a.completion === 'assigned' || a.completion === 'in_progress')
  const finished = crew.some((a) => a.completion === 'completed')
  if (atWork || !finished) return null

  const allHoursIn = job.estimatedHours !== null && job.completedHours >= job.estimatedHours
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border bg-surface px-4 py-3 shadow-chip">
      <span className="text-ui text-foreground">
        {allHoursIn ? 'Every hour of the work is in.' : 'The crew is settled — no one is at work on this job anymore.'}{' '}
        <span className="text-muted-foreground">The job can be completed.</span>
      </span>
      <span className="flex-1" />
      <Button size="sm" variant="primary" busy={move.isPending} onClick={() => move.mutate({ id: job.id, status: 'completed' })}>
        Mark the Job Completed
      </Button>
      {move.error ? <p className="w-full text-caption text-danger">{describeError(move.error)}</p> : null}
    </div>
  )
}

function AssignmentRow({ jobId, assignment }: { jobId: string; assignment: JobAssignment }) {
  const [editing, setEditing] = useState(false)
  const [ending, setEnding] = useState<'dropped' | 'removed' | null>(null)
  const [reason, setReason] = useState('')
  const move = useSetAssignmentCompletion()
  const state = partState(assignment)
  const name = assignment.playerDiscordName || assignment.playerEmail
  const atRest = ['completed', 'dropped', 'removed'].includes(assignment.completion)
  const completed = assignment.completion === 'completed' ? assignment.actualHours ?? assignment.estimatedHours ?? 0 : 0

  const changeStatus = (completion: AssignmentCompletion, droppedReason?: string) => {
    move.mutate({ jobId, assignmentId: assignment.id, input: { completion, ...(droppedReason ? { droppedReason } : {}) } }, {
      onSuccess: () => setEnding(null),
    })
  }

  return (
    <li className="py-2">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 sm:grid-cols-[auto_minmax(0,1fr)_auto_7rem_auto]">
        <Avatar name={name} email={assignment.playerEmail} src={assignment.playerPictureUrl} size="sm" />
        <span className="truncate text-ui font-medium text-foreground">{name}</span>
        <span className="col-start-2 row-start-2 text-caption text-muted-foreground sm:col-start-auto sm:row-start-auto">
          {assignment.estimatedHours === null ? 'Open-ended' : `${formatHours(assignment.estimatedHours)} assigned`}
          {' · '}{formatHours(completed)} completed
        </span>
        <div className="col-start-2 row-start-3 sm:col-start-auto sm:row-start-auto"><StatusLabel tone={state.tone} hue={state.hue} label={state.label} /></div>
        <div className="col-start-3 row-start-1 sm:col-start-auto sm:row-start-auto">
          <ActionMenu label={`Actions for ${name}`} sections={[
            { items: [{ key: 'edit', label: 'Edit Assignment', icon: <Icons.edit />, onSelect: () => setEditing(true) }] },
            { items: VERBS[assignment.completion].map(({ target, verb, asksReason }) => ({
              key: target, label: target === 'completed' && assignment.estimatedHours !== null ? `Mark ${formatHours(assignment.estimatedHours)} Completed` : verb, danger: target === 'removed', disabled: move.isPending,
              onSelect: () => {
                if (asksReason && (target === 'dropped' || target === 'removed')) {
                  move.reset()
                  setReason('')
                  setEnding(target)
                } else changeStatus(target)
              },
              ...(target === 'removed' ? { confirmLabel: 'Continue to Removal' } : {}),
            })) },
          ]} />
        </div>
      </div>
      {move.error && !ending ? <p role="alert" className="text-caption text-danger">{describeError(move.error)}</p> : null}
      <Dialog open={editing} onClose={() => setEditing(false)} labelledBy={`edit-${assignment.id}`} className="max-w-xl rounded-2xl border bg-surface p-5 shadow-modal">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h2 id={`edit-${assignment.id}`} className="font-display text-xl">Edit Assignment</h2>
          <IconButton label="Close editor" size="sm" onClick={() => setEditing(false)}><Icons.close /></IconButton>
        </div>
        {editing ? <>
          <AssignmentEditor jobId={jobId} assignment={assignment} atRest={atRest} />
          <HourAdjustment jobId={jobId} assignment={assignment} />
          <AssignmentNotes assignmentId={assignment.id} />
        </> : null}
      </Dialog>
      <Dialog open={ending !== null} onClose={() => { if (!move.isPending) setEnding(null) }} labelledBy={`end-${assignment.id}`} className="max-w-md rounded-2xl border bg-surface p-5 shadow-modal">
        <h2 id={`end-${assignment.id}`} className="font-display text-xl">{ending === 'removed' ? `Remove ${name}?` : 'Mark Assignment Dropped'}</h2>
        <form className="mt-4 space-y-3" onSubmit={(event) => {
          event.preventDefault()
          if (ending && !move.isPending && (ending !== 'dropped' || reason.trim())) changeStatus(ending, reason.trim() || undefined)
        }}>
          <Field label={ending === 'removed' ? 'Reason (Optional)' : 'Reason'} htmlFor={`reason-${assignment.id}`}>
            <TextField id={`reason-${assignment.id}`} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" disabled={move.isPending} onClick={() => setEnding(null)}>Cancel</Button>
            <Button size="sm" type="submit" variant={ending === 'removed' ? 'danger' : 'primary'} busy={move.isPending} disabled={ending === 'dropped' && !reason.trim()}>
              {ending === 'removed' ? 'Remove Player' : 'Confirm'}
            </Button>
          </div>
          {move.error ? <p role="alert" className="text-caption text-danger">{describeError(move.error)}</p> : null}
        </form>
      </Dialog>
    </li>
  )
}

/**
 * The working record for one player on one job: the money they are owed, the
 * hours, the quality of what came back. Everything here is editable mid-flight;
 * only what changed is sent. The payment *date* and status are the studio's
 * working note — the ledger of the next phase is the money truth.
 */
function AssignmentEditor({ jobId, assignment, atRest }: { jobId: string; assignment: JobAssignment; atRest: boolean }) {
  const [form, setForm] = useState(() => ({
    amount: assignment.paymentAmount === null ? '' : String(assignment.paymentAmount / 100),
    paymentStatus: assignment.paymentStatus,
    paymentDate: assignment.paymentDate ?? '',
    estimatedHours: assignment.estimatedHours === null ? '' : String(assignment.estimatedHours),
    quality: assignment.quality === null ? '' : String(assignment.quality),
    qualityNotes: assignment.qualityNotes ?? '',
    notes: assignment.notes ?? '',
  }))
  const update = useUpdateAssignment()
  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }))

  const amountInvalid = form.amount.trim() !== '' && !Number.isFinite(Number(form.amount))
  const hoursInvalid = (key: 'estimatedHours') =>
    form[key].trim() !== '' && (!Number.isFinite(Number(form[key])) || Number(form[key]) < 0)
  const counted = assignment.actualHours ?? (assignment.completion === 'completed' ? assignment.estimatedHours ?? 0 : 0)
  const belowLogged = form.estimatedHours.trim() !== '' && !hoursInvalid('estimatedHours') &&
    Number(form.estimatedHours) < counted

  const submit = () => {
    const input: Record<string, unknown> = {}
    const money = form.amount.trim() === '' ? null : Math.round(Number(form.amount) * 100)
    if (money !== assignment.paymentAmount) input.paymentAmount = money
    if (form.paymentStatus !== assignment.paymentStatus) input.paymentStatus = form.paymentStatus
    const paymentDate = form.paymentDate.trim() === '' ? null : form.paymentDate.trim()
    if (paymentDate !== assignment.paymentDate) input.paymentDate = paymentDate
    for (const key of ['estimatedHours'] as const) {
      const value = form[key].trim() === '' ? null : Number(form[key])
      if (value !== assignment[key]) input[key] = value
    }
    const quality = form.quality === '' ? null : Number(form.quality)
    if (quality !== assignment.quality) input.quality = quality
    if (form.qualityNotes.trim() !== (assignment.qualityNotes ?? '')) {
      input.qualityNotes = form.qualityNotes.trim() === '' ? null : form.qualityNotes.trim()
    }
    if (form.notes.trim() !== (assignment.notes ?? '')) {
      input.notes = form.notes.trim() === '' ? null : form.notes.trim()
    }
    if (Object.keys(input).length === 0) return
    update.mutate({ jobId, assignmentId: assignment.id, input: input as UpdateAssignmentInput })
  }

  return (
    <form
      className="grid gap-x-6 gap-y-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <Field
        label="Payment rate"
        htmlFor={`amount-${assignment.id}`}
        hint="In the job's currency."
        error={amountInvalid ? 'Numbers only — e.g. 1500.' : undefined}
      >
        <TextField
          id={`amount-${assignment.id}`}
          inputMode="decimal"
          value={form.amount}
          onChange={(event) => set('amount', event.target.value)}
        />
      </Field>
      <Field label="Payment status" htmlFor={`pay-status-${assignment.id}`}>
        <Dropdown
          id={`pay-status-${assignment.id}`}
          ariaLabel="Payment status"
          value={form.paymentStatus}
          options={[
            { value: 'unpaid', label: 'Unpaid' },
            { value: 'pending', label: 'Payment pending' },
            { value: 'paid', label: 'Paid' },
          ]}
          onChange={(value) => set('paymentStatus', value)}
        />
      </Field>
      <Field label="Payment date" htmlFor={`pay-date-${assignment.id}`}>
        <TextField
          id={`pay-date-${assignment.id}`}
          placeholder="YYYY-MM-DD"
          value={form.paymentDate}
          onChange={(event) => set('paymentDate', event.target.value)}
        />
      </Field>
      <Field
        label="Assigned Hours"
        htmlFor={`hours-${assignment.id}`}
        hint={`${formatHours(counted)} logged or counted as completed.`}
        error={belowLogged ? `Cannot assign fewer than ${formatHours(counted)} credited hours.` : undefined}
      >
        <div className="flex items-center gap-2">
          <TextField
            id={`hours-${assignment.id}`}
            inputMode="decimal"
            placeholder="Assigned"
            value={form.estimatedHours}
            onChange={(event) => set('estimatedHours', event.target.value)}
          />
        </div>
      </Field>
      {atRest ? (
        <>
          <Field label="Quality" htmlFor={`quality-${assignment.id}`} hint="Rate the work from 1 to 5.">
            <Dropdown
              id={`quality-${assignment.id}`}
              ariaLabel="Quality"
              value={form.quality}
              options={[
                { value: '', label: 'Not set' },
                ...[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} of 5` })),
              ]}
              onChange={(value) => set('quality', value)}
            />
          </Field>
          <Field label="Quality notes" htmlFor={`quality-notes-${assignment.id}`}>
            <TextField
              id={`quality-notes-${assignment.id}`}
              value={form.qualityNotes}
              onChange={(event) => set('qualityNotes', event.target.value)}
            />
          </Field>
        </>
      ) : null}
      <Field label="Notes" htmlFor={`notes-${assignment.id}`} className="sm:col-span-2">
        <TextArea id={`notes-${assignment.id}`} rows={2} value={form.notes} onChange={(event) => set('notes', event.target.value)} />
      </Field>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        <Button
          type="submit"
          size="md"
          variant="primary"
          busy={update.isPending}
          disabled={amountInvalid || hoursInvalid('estimatedHours') || belowLogged}
        >
          Save Changes
        </Button>
        {update.error ? <p className="text-caption text-danger">{describeError(update.error)}</p> : null}
      </div>
    </form>
  )
}

function HourAdjustment({ jobId, assignment }: { jobId: string; assignment: JobAssignment }) {
  const isAdmin = useSession().data?.account?.role === 'admin'
  const history = useAssignmentHourAdjustments(jobId, assignment.id)
  const deduct = useDeductAssignmentHours()
  const credited = assignment.actualHours ?? (assignment.completion === 'completed' ? assignment.estimatedHours ?? 0 : 0)
  const [open, setOpen] = useState(false)
  const [hours, setHours] = useState(0)
  const [reason, setReason] = useState('')
  const [evidenceUrl, setEvidenceUrl] = useState('')
  const validUrl = !evidenceUrl.trim() || /^https?:\/\//i.test(evidenceUrl.trim())
  return (
    <section className="mt-5 border-t border-hairline pt-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-caption text-muted-foreground">Logged: <strong className="text-foreground">{formatHours(credited)}</strong></p>
        {isAdmin && credited > 0 && assignment.completion !== 'removed' && assignment.completion !== 'dropped' ? (
          <Button size="xs" variant="ghost" onClick={() => setOpen((value) => !value)}>{open ? 'Close Adjustment' : 'Adjust Logged Hours'}</Button>
        ) : null}
      </div>
      {open && isAdmin ? (
        <form className="mt-3 rounded-xl border bg-surface-sunken p-4" onSubmit={(event) => {
          event.preventDefault()
          if (hours <= 0 || hours > credited || !reason.trim() || !validUrl) return
          deduct.mutate({ jobId, assignmentId: assignment.id, input: { hours, reason: reason.trim(), ...(evidenceUrl.trim() ? { evidenceUrl: evidenceUrl.trim() } : {}) } }, {
            onSuccess: () => { setOpen(false); setHours(0); setReason(''); setEvidenceUrl('') },
          })
        }}>
          <p className="text-ui font-medium">Deduct disputed hours</p>
          <p className="mt-1 text-caption text-muted-foreground">Assigned hours stay at {assignment.estimatedHours === null ? 'open ended' : formatHours(assignment.estimatedHours)}. Completed work reopens for the player.</p>
          <div role="group" aria-label="Hours to deduct" className="mt-3 flex w-fit items-center gap-2 rounded-full border bg-surface p-1">
            <Button size="xs" variant="ghost" disabled={hours <= 0} onClick={() => setHours(Math.max(0, hours - 1))}>−</Button>
            <output className="min-w-12 text-center font-mono text-caption">{formatHours(hours)}</output>
            <Button size="xs" variant="ghost" disabled={hours >= credited} onClick={() => setHours(Math.min(credited, hours + 1))}>+</Button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Reason for the player" htmlFor={`adjust-reason-${assignment.id}`} className="sm:col-span-2">
              <TextArea id={`adjust-reason-${assignment.id}`} rows={2} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} />
            </Field>
            <Field label="Evidence link (optional)" htmlFor={`adjust-evidence-${assignment.id}`} error={!validUrl ? 'Use an https:// link.' : undefined}>
              <TextField id={`adjust-evidence-${assignment.id}`} inputMode="url" value={evidenceUrl} onChange={(event) => setEvidenceUrl(event.target.value)} />
            </Field>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button type="submit" size="md" variant="danger" busy={deduct.isPending} disabled={hours <= 0 || hours > credited || !reason.trim() || !validUrl}>Apply Deduction</Button>
            {deduct.error ? <p className="text-caption text-danger">{describeError(deduct.error)}</p> : null}
          </div>
        </form>
      ) : null}
      {history.data?.length ? (
        <div className="mt-4">
          <p className="text-caption font-medium">Hour adjustment history</p>
          <ul className="mt-2 space-y-2">
            {history.data.map((entry) => <li key={entry.id} className="text-caption text-muted-foreground">
              {formatTimestamp(entry.createdAt)} · {entry.actorName ?? 'Admin'} · {formatHours(entry.from)} → {formatHours(entry.to)}. {entry.reason}
              {entry.evidenceUrl ? <> · <a className="underline" href={entry.evidenceUrl} target="_blank" rel="noopener noreferrer">Evidence</a></> : null}
            </li>)}
          </ul>
        </div>
      ) : null}
    </section>
  )
}

/** The words on one assignment — the player's notes from their task among them. Shown only when there are any. */
function AssignmentNotes({ assignmentId }: { assignmentId: string }) {
  const notes = useNotes('assignment', assignmentId)
  if (!notes.data?.length) return null
  return (
    <section className="mt-4">
      <p className="text-micro text-muted-foreground uppercase">Notes</p>
      <ul className="mt-1.5 flex flex-col gap-2">
        {notes.data.map((note) => (
          <li key={note.id} className="rounded-xl bg-tint px-3 py-2">
            <p className="text-caption text-muted-foreground">
              {note.authorName ?? 'Someone'} · {formatTimestamp(note.createdAt)}
            </p>
            <p className="mt-0.5 whitespace-pre-wrap text-ui text-foreground">{note.body}</p>
          </li>
        ))}
      </ul>
    </section>
  )
}
